import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, CheckCircle, ArrowRight } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';
import { LocalServiceStructuredData } from '../components/StructuredData';
import { getPageMeta } from '../lib/seo';

// Reusable local service page template
export const LocalServicePage = ({
  areaName,
  areaType = "City",
  slug,
  heroTitle,
  heroSubtitle,
  introContent,
  servicesEmphasis,
  localContextTitle,
  localContext,
  commonProblems,
  whyChooseUs,
  ctaText
}) => {
  useScrollAnimations();

  const description = getPageMeta(`/${slug}`).description;

  return (
    <>
      
      <LocalServiceStructuredData 
        areaName={areaName}
        areaType={areaType}
        description={description}
        slug={slug}
      />

      {/* Hero Section */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        color: 'white',
        padding: '80px 0 60px'
      }}>
        <div className="royal-container scroll-animate">
          <h1 style={{
            fontSize: 'clamp(2rem, 5vw, 3rem)',
            fontWeight: '700',
            marginBottom: '20px',
            lineHeight: '1.2',
            color: 'var(--royal-white)'
          }}>
            {heroTitle}
          </h1>
          <p style={{
            fontSize: '1.2rem',
            color: 'rgba(255,255,255,0.9)',
            maxWidth: '700px',
            marginBottom: '30px',
            lineHeight: '1.7'
          }}>
            {heroSubtitle}
          </p>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <a href="tel:07710613783" className="royal-btn-primary" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Phone size={20} />
              Call 07710 613783
            </a>
            <Link to="/contact" className="royal-btn-secondary" style={{
              background: 'transparent',
              border: '2px solid white',
              color: 'white'
            }}>
              Request a Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section style={{ padding: '60px 0', background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div className="scroll-animate" style={{ maxWidth: '900px' }}>
            {introContent}
          </div>
        </div>
      </section>

      {/* Services Emphasis */}
      <section style={{ padding: '60px 0', background: 'var(--bg-light)' }}>
        <div className="royal-container">
          <h2 className="scroll-animate" style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: 'var(--royal-navy)',
            marginBottom: '40px',
            textAlign: 'center'
          }}>
            Our Services in {areaName}
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '24px'
          }}>
            {servicesEmphasis.map((service, index) => (
              <div key={index} className="scroll-animate" style={{
                background: 'white',
                padding: '30px',
                borderRadius: '12px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)'
              }}>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '600',
                  color: 'var(--royal-navy)',
                  marginBottom: '12px'
                }}>
                  {service.title}
                </h3>
                <p style={{
                  color: 'var(--text-secondary)',
                  lineHeight: '1.7'
                }}>
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Local Context */}
      <section style={{ padding: '60px 0', background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div className="scroll-animate" style={{ maxWidth: '900px' }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'var(--royal-navy)',
              marginBottom: '24px'
            }} data-testid="local-context-heading">
              {localContextTitle}
            </h2>
            {localContext}
          </div>
        </div>
      </section>

      {/* Common Problems */}
      <section style={{ padding: '60px 0', background: 'var(--bg-light)' }}>
        <div className="royal-container">
          <h2 className="scroll-animate" style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            color: 'var(--royal-navy)',
            marginBottom: '40px'
          }}>
            Common Issues We Resolve
          </h2>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '20px'
          }}>
            {commonProblems.map((problem, index) => (
              <div key={index} className="scroll-animate" style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '20px',
                background: 'white',
                borderRadius: '8px'
              }}>
                <CheckCircle size={24} style={{ color: 'var(--royal-gold)', flexShrink: 0, marginTop: '2px' }} />
                <div>
                  <strong style={{ color: 'var(--royal-navy)' }}>{problem.title}</strong>
                  <p style={{ color: 'var(--text-secondary)', marginTop: '4px', fontSize: '0.95rem' }}>
                    {problem.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section style={{ padding: '60px 0', background: 'var(--royal-white)' }}>
        <div className="royal-container">
          <div className="scroll-animate" style={{ maxWidth: '900px' }}>
            <h2 style={{
              fontSize: '1.75rem',
              fontWeight: '700',
              color: 'var(--royal-navy)',
              marginBottom: '24px'
            }}>
              Why {areaName} Residents Trust Royal Security
            </h2>
            {whyChooseUs}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{
        padding: '60px 0',
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        color: 'white'
      }}>
        <div className="royal-container scroll-animate" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            marginBottom: '16px'
          }}>
            {ctaText}
          </h2>
          <p style={{
            fontSize: '1.1rem',
            color: 'rgba(255,255,255,0.9)',
            marginBottom: '30px',
            maxWidth: '600px',
            margin: '0 auto 30px'
          }}>
            Contact us for honest advice, accurate diagnosis, and quality repairs that last.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a href="tel:07710613783" className="royal-btn-primary" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <Phone size={20} />
              07710 613783
            </a>
            <a href="mailto:Joe@RoyalSecurity.info" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'white',
              textDecoration: 'none'
            }}>
              <Mail size={20} />
              Joe@RoyalSecurity.info
            </a>
          </div>
          <div style={{ marginTop: '30px' }}>
            <Link to="/services" style={{
              color: 'var(--royal-gold)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              textDecoration: 'none'
            }}>
              View All Services <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};
